import { useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';

import VideoCard from '#client/components/VideoCard/VideoCard';
import { buildCartUrl } from '#client/utils/urls';

import Section from './components/Section';

import { appendUrlParam } from '../Home/utils';
import {
  LD_RELEASE_TRY_BEFORE_YOU_BUY,
  MARKETING_TOOLS_MODAL_PARAM_KEY,
  MT_DATA_TRACKING,
  WEBSITE_PRODUCTS,
  websiteEditorModalParam
} from './utils/constants';

const StyledVideoCard = styled(VideoCard)(({ theme }) => ({
  '&.VideoCard-root': {
    flex: 1,
    marginTop: 0
  },
  '& .VideoCard-cardContainer': {
    padding: 0,
    flexDirection: 'row-reverse',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: 'unset'
    }
  },
  '& .VideoCard-trackButton': {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: '-webkit-box'
    }
  },
  '& .VideoCard-trackButton-text': {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '9rem'
  },
  '& .VideoCard-trackButton-icon': {
    display: 'none'
  },
  '& .VideoCard-videoDescription': {
    [theme.breakpoints.down('md')]: {
      '&::after': {
        position: 'absolute',
        content: '""',
        width: '1px',
        height: '100%',
        backgroundColor: theme.palette.focus30.light,
        top: '0',
        right: '7.5rem'
      }
    }
  },
  '& .VideoCard-caption': {
    color: theme.palette.focus50.main,
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: 'block'
    }
  }
}));

const Website = ({ onLearnMoreClick }) => {
  const { businessEntityUuid } = useBusinessEntity();
  const location = useLocation();
  const navigate = useNavigate();
  const releaseTryBeforeYouBuy = useExperimentFlag(LD_RELEASE_TRY_BEFORE_YOU_BUY, false);

  const [showAnimatedGIF, setShowAnimatedGIF] = useState(
    // Get initial value from user preferences
    () => window.matchMedia('(prefers-reduced-motion: no-preference)')?.matches === true
  );

  const handleImageClickTracking = useButtonPressedCallback({
    ...MT_DATA_TRACKING,
    elementName: 'view_type_toggle'
  });
  const handleImageClick = (event) => {
    handleImageClickTracking(event);
    setShowAnimatedGIF((value) => !value);
  };

  const handleCTAClickTracking = useButtonPressedCallback({
    ...MT_DATA_TRACKING,
    elementName: releaseTryBeforeYouBuy ? 'try_website_builder_free' : 'get_website_builder'
  });
  const handleCTAClick = (event) => {
    handleCTAClickTracking(event);
    if (releaseTryBeforeYouBuy) {
      appendUrlParam({
        navigate,
        location,
        paramName: websiteEditorModalParam,
        paramKey: MARKETING_TOOLS_MODAL_PARAM_KEY
      });
    }
  };

  const handleOpenVideoClickTracking = useButtonPressedCallback({
    ...MT_DATA_TRACKING,
    elementName: 'how_to_video'
  });
  const handleOpenVideoClick = (event) => {
    handleOpenVideoClickTracking(event);
  };

  const buyWebsiteBuilderUrl = buildCartUrl(WEBSITE_PRODUCTS.WEBSITE, { businessEntityUuid });

  return (
    <Section
      title="Look legit and stand out online"
      description="Create a custom website with built-in features that drive business. No coding required."
    >
      <Box
        borderRadius={4}
        px={3}
        py={2}
        boxShadow="0px 4px 16px 2px rgba(0, 0, 0, 0.12)"
        display="flex"
        flexDirection="column"
        gap={2}
      >
        <Box display="flex" alignItems="center">
          <StyledVideoCard
            video="website"
            onOpen={handleOpenVideoClick}
            LinkButtonProps={{
              href: null,
              target: null,
              rel: null,
              component: 'button',
              onClick: onLearnMoreClick
            }}
          />
          <Button
            size="medium"
            color="Focus"
            variant="contained"
            href={
              releaseTryBeforeYouBuy
                ? null
                : buyWebsiteBuilderUrl
            }
            onClick={handleCTAClick}
            sx={{
              display: {
                xs: 'none',
                md: 'block'
              }
            }}
          >
            {releaseTryBeforeYouBuy ? 'Try website builder for FREE' : 'Get website builder'}
          </Button>
        </Box>
        <Box onClick={handleImageClick}>
          {/*
              NOTE: Inline styles to hide/show images instead of conditional rendering,
              so both images load at the same time thus avoid flickering effect
              when user toggle them on the first time.
          */}
          <img
            style={{ display: showAnimatedGIF ? 'block' : 'none' }}
            alt="Website builder animation"
            width="100%"
            src="https://res.cloudinary.com/zenbusiness/q_auto:best,f_auto,w_auto/v1/shared-assets/animation/gif/duda-preview.gif"
          />
          <img
            style={{ display: showAnimatedGIF ? 'none' : 'block' }}
            alt="Website builder"
            width="100%"
            src="https://res.cloudinary.com/zenbusiness/q_auto:best,f_png,w_auto/v1/shared-assets/animation/gif/duda-preview.gif"
          />
        </Box>
        <Button
          size="medium"
          variant="contained"
          fullWidth
          disabled
          sx={{
            textAlign: 'center',
            display: {
              xs: 'block',
              md: 'none'
            }
          }}
        >
          Free to use on desktop
        </Button>
      </Box>
    </Section>
  );
};

Website.propTypes = {
  onLearnMoreClick: PropTypes.func.isRequired
};

export default Website;
